import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import apiClient from "../../axios";

const BankAccountForm = () => {
    const [formData, setFormData] = useState({
        accountHolderName: "",
        accountNumber: "",
        confirmAccountNumber: "",
        ifscCode: "",
        bankName: "",
        branchName: "",
    });

    const [errors, setErrors] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [apiError, setApiError] = useState("");
    const [isUpdateMode, setIsUpdateMode] = useState(false); // Track if we're updating an existing record

    const ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/; // IFSC Code format
    const accountNumberPattern = /^[0-9]{9,18}$/; // 9-18 digit account number

    useEffect(() => {
        // Fetch existing bank details
        const fetchBankDetails = async () => {
            try {
                const response = await apiClient.get("/bank/account/details");
                console.log(response);

                if (response.status === 200 && response.data?.data?.University_details.length > 0) {
                    const details = response.data.data.University_details[0];
                    setFormData({
                        accountHolderName: details.ac_name || "",
                        accountNumber: details.ac_number.toString() || "",
                        confirmAccountNumber: details.ac_number.toString() || "",
                        ifscCode: details.ifsc_code || "",
                        bankName: details.bank_name || "",
                        branchName: details.branch_name || "",
                    });
                    setIsUpdateMode(true); // Set update mode if data exists
                }
            } catch (error) {
                console.error("Error fetching bank details:", error);
            }
        };

        fetchBankDetails();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setErrors({ ...errors, [name]: "" }); // Clear error as user types
    };

    const validateForm = () => {
        let formErrors = {};

        if (!formData.accountHolderName.trim()) {
            formErrors.accountHolderName = "Account holder name is required.";
        }
        if (!formData.accountNumber.trim() || !accountNumberPattern.test(formData.accountNumber)) {
            formErrors.accountNumber =
                "Account number is required and must be 9-18 digits.";
        }
        if (formData.accountNumber !== formData.confirmAccountNumber) {
            formErrors.confirmAccountNumber = "Account numbers do not match.";
        }
        if (!formData.ifscCode.trim() || !ifscPattern.test(formData.ifscCode)) {
            formErrors.ifscCode =
                "IFSC code is required and must follow the correct format (e.g., ABCD0123456).";
        }
        if (!formData.bankName.trim()) {
            formErrors.bankName = "Bank name is required.";
        }
        if (!formData.branchName.trim()) {
            formErrors.branchName = "Branch name is required.";
        }
        setErrors(formErrors);
        return Object.keys(formErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (validateForm()) {
            setIsSubmitted(false);
            setApiError(""); 
            const payload = {
                ac_name: formData.accountHolderName,
                ac_number: formData.accountNumber,
                ifsc_code: formData.ifscCode,
                bank_name: formData.bankName,
                branch_name: formData.branchName,
            };

            try {
                const response = isUpdateMode
                    ? await apiClient.post("/bank/detials/update", payload)
                    : await apiClient.post("/bank/details/add", payload);

                // console.log("API Response:", response.data);

                if (response.status === 200) {
                    setIsSubmitted(true);
                } else {
                    setApiError(response.data.message || "Submission failed.");
                }
            } catch (error) {
                console.error("Error submitting form:", error);
                setApiError(error.response?.data?.message || "An unexpected error occurred.");
            }
        } else {
            setIsSubmitted(false);
        }
    };

    return (
        <div className="container-fuild mt-5 d-flex">
            <div className="card shadow-lg" style={{ width: "100%" }}>
                <div className="card-header text-center bg-primary text-white">
                    <h3>{isUpdateMode ? "Update Your Bank Details" : "Enter Your Bank Details"}</h3>
                </div>
                <div className="card-body p-4">
                    {isSubmitted && (
                        <div className="alert alert-success text-center" role="alert">
                            Form {isUpdateMode ? "updated" : "submitted"} successfully!
                        </div>
                    )}
                    {apiError && (
                        <div className="alert alert-danger text-center" role="alert">
                            {apiError}
                        </div>
                    )}
                    <div className="row g-3">
                        {/* Account Holder Name */}
                        <div className="col-md-12">
                            <label className="form-label fw-bold">Account Holder Name:</label>
                            <input
                                type="text"
                                name="accountHolderName"
                                value={formData.accountHolderName}
                                onChange={handleChange}
                                className={`form-control ${errors.accountHolderName ? "is-invalid" : ""}`}
                                placeholder="Enter your full name"
                            />
                            {errors.accountHolderName && (
                                <div className="invalid-feedback">{errors.accountHolderName}</div>
                            )}
                        </div>

                        {/* Account Number */}
                        <div className="col-md-12">
                            <label className="form-label fw-bold">Account Number:</label>
                            <input
                                type="text"
                                name="accountNumber"
                                value={formData.accountNumber}
                                onChange={handleChange}
                                className={`form-control ${errors.accountNumber ? "is-invalid" : ""}`}
                                placeholder="Enter your account number"
                            />
                            {errors.accountNumber && (
                                <div className="invalid-feedback">{errors.accountNumber}</div>
                            )}
                        </div>

                        {/* Confirm Account Number */}
                        <div className="col-md-12">
                            <label className="form-label fw-bold">Confirm Account Number:</label>
                            <input
                                type="text"
                                name="confirmAccountNumber"
                                value={formData.confirmAccountNumber}
                                onChange={handleChange}
                                className={`form-control ${errors.confirmAccountNumber ? "is-invalid" : ""}`}
                                placeholder="Re-enter your account number"
                            />
                            {errors.confirmAccountNumber && (
                                <div className="invalid-feedback">{errors.confirmAccountNumber}</div>
                            )}
                        </div>

                        {/* IFSC Code */}
                        <div className="col-md-12">
                            <label className="form-label fw-bold">IFSC Code:</label>
                            <input
                                type="text"
                                name="ifscCode"
                                value={formData.ifscCode}
                                onChange={handleChange}
                                className={`form-control ${errors.ifscCode ? "is-invalid" : ""}`}
                                placeholder="Enter the IFSC code"
                            />
                            {errors.ifscCode && (
                                <div className="invalid-feedback">{errors.ifscCode}</div>
                            )}
                        </div>

                        {/* Bank Name */}
                        <div className="col-md-12">
                            <label className="form-label fw-bold">Bank Name:</label>
                            <input
                                type="text"
                                name="bankName"
                                value={formData.bankName}
                                onChange={handleChange}
                                className={`form-control ${errors.bankName ? "is-invalid" : ""}`}
                                placeholder="Enter the bank name"
                            />
                            {errors.bankName && (
                                <div className="invalid-feedback">{errors.bankName}</div>
                            )}
                        </div>

                        {/* Branch Name */}
                        <div className="col-md-12">
                            <label className="form-label fw-bold">Branch Name:</label>
                            <input
                                type="text"
                                name="branchName"
                                value={formData.branchName}
                                onChange={handleChange}
                                className={`form-control ${errors.branchName ? "is-invalid" : ""}`}
                                placeholder="Enter the branch name"
                            />
                            {errors.branchName && (
                                <div className="invalid-feedback">{errors.branchName}</div>
                            )}
                        </div>
                    </div>

                    <div className="text-center mt-4">
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="btn btn-success btn-lg w-100"
                        >
                            {isUpdateMode ? "Update" : "Submit"}
                        </button>
                    </div>
                </div>
            </div>
            <div className="notes p-5">
                <div className="card shadow-sm">
                    <div className="card-header bg-info text-white">
                        <h4>Important Notes</h4>
                    </div>
                    <div className="card-body">
                        <ul>
                            <li>Please ensure all information provided is accurate and matches your bank records.</li>
                            <li>The account number must be between 9-18 digits. Double-check before submission.</li>
                            <li>IFSC codes are case-sensitive and typically follow the format: <strong>ABCD0123456</strong>.</li>
                            <li>For any assistance, contact your bank's customer service or visit the nearest branch.</li>
                            <li>Your data is securely handled and will not be shared without your consent.</li>
                        </ul>
                        <p className="text-muted">
                            If you experience any issues, please contact support at <strong>karthik@theleadsense.com</strong>.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BankAccountForm;
